import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AgGridReact } from '@ag-grid-community/react';
import { RowNode } from '@ag-grid-community/core';

import { Box, H5, Skeleton, Snackbar } from '@parspec/pixel';

import Footer from '../Footer';
import MainProductTable from './mainProductTable/MainProductTable';
import FreightModal from './freightModal';
import LotPricingModal from './lotPriceModal';
import { ModalCard } from './modalCard';
import ServiceModalWrapper from './servicesModal';
import TaxesModal from './taxesModal';
import { useCheckAccessForProductFinderOrQuoting } from '../../shared/hooks/useCheckAccessForProductFinderOrQuoting';

import { updateComponentsPricingData, usePricingBodPatchMutation } from './queries';
import { saveColumnOrderEvents, useUpdateBomDataMutation, useUpdateGrandTotalMutation } from '../queries';
import { getApiDataForBomInfo, getApiDataForPricingInfo, getPriceStats, getServicesTotal } from './shared/utils';
import { MainProductDataType } from './mainProductTable';
import { checkDefaultValuesWarningCount, getAmountForUi, applyRounding } from '../shared/utils';
import { convertToCents, convertToDollar } from '../../shared/utils/utils';
import { useGetFrieghtDetailsQuery } from './freightModal/queries';
import { updateLotQueryData, usePutLotsMutation } from './lotPriceModal/queries';
import { useServiceQuery } from './servicesModal/queries';
import { useTaxesQuery } from './taxesModal/queries';
import { TAX_CALC_VALUES } from './taxesModal/constants';
import { CALC_METHODS, COLUMN_OPTIONS_PL_TAB } from './shared/constants';
import OptionTable from './optionsTable/OptionsTable';
import { useCustomisationColumn, useGetCustomisedColumnData, useProjectBomsMappedData } from '../shared/hooks';
import { OptionDataType } from './optionsTable';
import { useFeatureFlag } from '../../shared/UserActivityTracking/useFeatureFlag';
import LinkedBomsPopup from '../shared/LinkedBomsPopup';
import { useGetUserProfileInfoQuery } from 'src/features/Settings/MyProfile/queries';
import { useGetSelectedColumnsAndPersistingFiltersQuery } from 'src/features/shared/queries';
import { ManageColumnsPopup } from 'src/features/Dashboard/shared/components/ManageColumnsPopup/ManageColumnsPopup';
import KebabNestedMenu from 'src/features/BOM/shared/KebabNestedMenu';
import { DEFAULT_VALUES_WARNING } from 'src/features/Dashboard/shared/constants';
import { ColumnKeys } from '../queries/apiTypes';
import { RoundingPayloadProps, RoundingPayloadRequest } from './queries/apiTypes';
import { NO_ROUNDING, ROUNDING_SELL_PRICE_KEY, BOM_TABLES_ENUM } from '../shared/constants';
import { useBulkEditPatchMutation } from './bulkEdit/bulkEditPopup/queries';
import { handlePricingColumnChange } from './mainProductTable/utils';
import { useGetProjectBomsQuery } from 'src/features/Project/queries';
import ProductOrOptionTableContent from '../shared/ProductOrOptionTableContent';
import { LotsResponse, ProcessedItem, RoundingBehaviour } from '../shared/types';
import ImportProducts from '../shared/ImportProducts';
import { VIEW_OPTIONS, ViewOptionValues } from './shared/ViewBySelect';
import ManufacturerViewTable from './manufacturerViewTable/ManufacturerViewTable';
import useResize from 'src/features/shared/hooks/useResize';
import { useBomAllTableData } from '../shared/hooks/useBomAllTableData';

interface TablesView {
    productTable: ViewOptionValues,
    optionsTable: ViewOptionValues,
};

export function PricingAndLeadTimeTab() {
    const { bomId = '0', projectId } = useParams();
    const navigate = useNavigate();

    const { enable_quoting } = useFeatureFlag();
    const { quotingAccess, isLoading } = useCheckAccessForProductFinderOrQuoting();

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (!enable_quoting || !quotingAccess) {
            navigate('/v2/dashboard');
        }
    }, [quotingAccess, enable_quoting, isLoading, navigate]);

    const [openLotModal, setOpenLotModal] = useState(false);
    const [openServicesModal, setOpenServicesModal] = useState(false);
    const [openTaxesModal, setOpenTaxesModal] = useState(false);
    const [openFreightModal, setOpenFreightModal] = useState(false);
    // const [tableMaxHeight, setTableMaxHeight] = useState(300);
    const [openManageColumnsPopup, setOpenManageColumnsPopup] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [tablesView, setTablesView] = useState<TablesView>({
        productTable: VIEW_OPTIONS.SECTION,
        optionsTable: VIEW_OPTIONS.SECTION,
    });
    const selectedRowsRef = useRef<Set<number>>(new Set());
    const currentGroupWhilePastingRef = useRef<number | null>(null);
    const mainProductRef = useRef<AgGridReact>(null);
    const optionProductRef = useRef<AgGridReact>(null)

    const { mutateAsync: updateBomData } = useUpdateBomDataMutation();
    const { mutateAsync: updatePricingData } = usePricingBodPatchMutation();
    const { data: userData } = useGetUserProfileInfoQuery();
    const { mutateAsync: saveLotDetails, isLoading: isSaveLoading } = usePutLotsMutation();
    const [tableContainerDimensions, resizeNodeSetter] = useResize();

    const { section, option, sectionData, totalExtendedPrice, totalCost, totalExtDiscountedCost, totalMarginCost, totalMarginPercentage, mainProductOptionsInfo, lotApiData, isLotDataFetching, isSectionLoading, unProcessedTableData, optionsApiData, isDataLoading, currentTabValue } = useBomAllTableData(bomId)

    const { isFetching: isSelectedColumnsAndFiltersQueryDataFetching } = useGetSelectedColumnsAndPersistingFiltersQuery(
        { userId: userData?.data?.id || 0, pageName: 'bom_pricing_tab' },
        { enabled: true }
    );
    const { columnOrderDetails, isColumnDataLoading } = useGetCustomisedColumnData(bomId, 'bom_pricing_tab');
    const { mutateAsync: updateGrandTotal } = useUpdateGrandTotalMutation();

    const { refetch } = useGetProjectBomsQuery(Number(projectId), {
        enabled: false
    });

    const { data: servicesData, isFetching: isServicesFetching, isLoading: isServicesLoading } = useServiceQuery(Number(bomId), { enabled: Boolean(bomId) });
    const { data: taxesData, isFetching: isTaxesFetching, isLoading: isTaxesLoading } = useTaxesQuery(Number(bomId), { enabled: Boolean(bomId) });
    const { data: freightDetails, isFetching: isFreightFetching, isLoading: isFreightLoading } = useGetFrieghtDetailsQuery(bomId);
    const { handleTablesReordering, handleTablesWidthChanged } = useCustomisationColumn();

    const { mutateAsync: patchBulkEdit, isLoading: isRoundingLoading } = useBulkEditPatchMutation();
    const { projectBomsAndPrimaryData } = useProjectBomsMappedData(Number(projectId));
    const { projectBomsMappedData, primaryBomId } = projectBomsAndPrimaryData;
    const isFooterLoading = isDataLoading || isServicesLoading || isTaxesLoading || isFreightLoading;

    const lotCardData = useMemo(() => {
        const lotsExtendedCostTotal = lotApiData?.data.reduce((acc: number | null, row) => {
            if (row.sell_price_cents !== null) {
                return Number(acc) + row.sell_price_cents;
            }
            return acc;
        }, null);
        const lotAmount = lotApiData?.data?.length === 0 || lotsExtendedCostTotal === null ? null : convertToDollar(lotsExtendedCostTotal || 0);
        const lotsLength = lotApiData?.data?.length || 0;
        return {
            title: 'Lots',
            amount: Number(lotAmount?.toFixed(2) || null),
            amountForUi: lotAmount === null ? null : getAmountForUi(lotAmount),
            helperText: `${lotsLength === 1 ? 'LOT' : 'LOTS'} (${lotsLength})`,
            editIconCallBack: () => setOpenLotModal(true),
            loading: isLotDataFetching
        };
    }, [lotApiData?.data, isLotDataFetching]);

    const serviceCardData = useMemo(() => {
        const servicesTotal = getServicesTotal(servicesData?.data, Number(totalExtendedPrice.toFixed(2)));
        const servicesLength = servicesData?.data?.length || 0;
        return {
            title: 'Services',
            amount: Number(servicesTotal?.toFixed(2) || null),
            amountForUi: servicesTotal || servicesTotal === 0 ? getAmountForUi(servicesTotal) : null,
            helperText: `${servicesLength === 1 ? 'SERVICE' : 'SERVICES'} (${servicesLength})`,
            editIconCallBack: () => setOpenServicesModal(true),
            loading: isServicesFetching
        };
    }, [servicesData?.data, totalExtendedPrice, isServicesFetching]);

    const freightCardData = useMemo(() => {
        let freightAmount = null;
        if (freightDetails?.data) {
            const { calculation_method, flat_fee_cents, percentage_of_order } = freightDetails.data || {};

            if (calculation_method === CALC_METHODS.FLAT_FEE && flat_fee_cents !== null) {
                freightAmount = convertToDollar(flat_fee_cents);
            } else if (calculation_method === CALC_METHODS.PERCENTAGE_OF_ORDER && percentage_of_order !== null) {
                freightAmount = (Number(totalExtendedPrice.toFixed(2)) * percentage_of_order) / 100;
            }
        }

        return {
            title: 'Freight',
            amount: Number(freightAmount?.toFixed(2) || null),
            amountForUi: freightAmount === null ? freightAmount : getAmountForUi(freightAmount),
            helperText: 'FREIGHT',
            editIconCallBack: () => setOpenFreightModal(true),
            loading: isFreightFetching
        };
    }, [freightDetails?.data, totalExtendedPrice, isFreightFetching]);

    const taxCardData = useMemo(() => {
        const taxType = taxesData?.data.calculation_method;
        let totalAmount = 0;
        if (taxType === TAX_CALC_VALUES.MANUAL_ENTRY) {
            if (taxesData?.data?.apply_taxes_to_products) {
                totalAmount += Number(totalExtendedPrice.toFixed(2));
            }

            if (taxesData?.data?.apply_taxes_to_services) {
                totalAmount += Number(serviceCardData.amount);
            }

            if (taxesData?.data?.apply_taxes_to_freight) {
                totalAmount += Number(freightCardData.amount);
            }
        }
        const totalTaxAmount =
            taxType !== TAX_CALC_VALUES.TAX_EXEMPT && (taxesData?.data.tax_percentage || taxesData?.data.tax_percentage === 0) ? (totalAmount * taxesData?.data.tax_percentage) / 100 : null;
        return {
            title: 'Taxes',
            amount: Number(totalTaxAmount?.toFixed(2) || null),
            amountForUi: totalTaxAmount === null ? totalTaxAmount : getAmountForUi(totalTaxAmount),
            helperText: taxType === TAX_CALC_VALUES.TAX_EXEMPT ? 'TAXES EXCLUDED' : `${taxesData?.data.tax_percentage || 0}% TAX RATE`,
            editIconCallBack: () => setOpenTaxesModal(true),
            loading: isTaxesFetching
        };
    }, [taxesData?.data, serviceCardData, freightCardData, totalExtendedPrice, isTaxesFetching]);

    const grandTotal = Number(totalExtendedPrice.toFixed(2)) + Number(taxCardData.amount) + Number(freightCardData.amount) + Number(serviceCardData.amount);

    const makeApiCall = useCallback(async () => {
        const payload = {
            bomId: String(bomId),
            input: {
                grand_total_cents: convertToCents(grandTotal),
                timestamp: Date.now() / 1000
            }
        };
        await updateGrandTotal(payload);
        refetch();
    }, [bomId, grandTotal, refetch, updateGrandTotal]);

    useEffect(() => {
        if (typeof grandTotal === 'number') {
            makeApiCall();
        }
    }, [grandTotal, makeApiCall]);

    const cardDataArr = useMemo(() => [lotCardData, serviceCardData, freightCardData, taxCardData], [lotCardData, serviceCardData, freightCardData, taxCardData]);

    const handlePricingInfoEdit = useCallback(
        async function (rowData: Partial<Omit<MainProductDataType, 'qty' | 'type' | 'manufacturer' | 'modelNo' | 'description' | 'specification' | 'lotInfo'>>, shouldCancelQueries = true) {
            const { id: bodId = 0, ...restRowInfo } = rowData;
            const apiReqData = getApiDataForPricingInfo(restRowInfo);
            if (bodId && apiReqData) {
                await updatePricingData({ bomId: Number(bomId), pricingBod: apiReqData, bodId: bodId, shouldCancelQueries });
            }
        },
        [updatePricingData, bomId]
    );

    const handleBomInfoEdit = useCallback(
        async function (
            rowData: Partial<Pick<MainProductDataType, 'qty' | 'category' | 'description' | 'manufacturer' | 'model_number' | 'productFinder' | 'specification' | 'id'>> & Partial<Pick<OptionDataType, 'replaceProductId'>>
        ) {
            const apiReqData = getApiDataForBomInfo(rowData);
            await updateBomData({ bomId: String(bomId), input: apiReqData });
        },
        [updateBomData, bomId]
    );

    const handleLotUpdate = useCallback(
        function ({ bodId, prevLotId, currLotId, componentIds }: { bodId: number; prevLotId: string; currLotId: string; componentIds?: Set<number> }) {
            const lotIdToAdd = currLotId === '-' ? null : Number(currLotId);
            const lotIdToDelete = prevLotId === '-' ? null : Number(prevLotId);
            const pricingInfoToUpdate =
                lotIdToAdd === null
                    ? {
                        lotId: lotIdToAdd,
                        id: bodId
                    }
                    : {
                        lotId: lotIdToAdd,
                        id: bodId,
                        cost: null,
                        discount: null,
                        discountedCost: null,
                        extendedDiscountCost: null,
                        margin: null,
                        sellPrice: null,
                        extendedPrice: null
                    };
            updateLotQueryData(bomId || '0', lotIdToAdd, lotIdToDelete);
            if (componentIds) {
                updateComponentsPricingData(Number(bomId), componentIds);
            }
            handlePricingInfoEdit(pricingInfoToUpdate);
        },
        [handlePricingInfoEdit, bomId]
    );

    const handleNewLot = useCallback(() => setOpenLotModal(true), []);

    const statsData = isFooterLoading || isSaveLoading
        ? []
        : isSectionLoading
            ? getPriceStats({})
            : getPriceStats({ totalExtendedPrice, totalCost, totalExtDiscountedCost, totalMarginCost, totalMarginPercentage, grandTotal });

    const prepareRoundingPayload = useCallback((updatedPriceInfo: RoundingPayloadProps) => {
        const { qty, sellPrice, id, margin } = updatedPriceInfo;

        if (qty || sellPrice) {
            const data = {
                bod_data: {
                    bod_id: id
                },
                metadata: {
                    margin_percentage: margin,
                    sell_price_cents: sellPrice !== null && String(sellPrice).length > 0 ? convertToCents(sellPrice) : null
                }
            };
            return data;
        }
        return;
    }, [])

    const processDataForRoundngPayload = useCallback((payload: RoundingPayloadRequest[], product: MainProductDataType, roundingBehviour: string, roundingPrecision: string) => {
        const isSellPrice = roundingBehviour === ROUNDING_SELL_PRICE_KEY;
        const updatedPriceInfo = handlePricingColumnChange(
            { ...product, roundingPrecision, roundingBehaviourOnCell: roundingBehviour },
            BOM_TABLES_ENUM.SELL_PRICE,
            true
        );

        if (isSellPrice) {
            const payloadData = prepareRoundingPayload({ ...updatedPriceInfo, id: product.id });
            if (payloadData) payload.push(payloadData);
        }

        return updatedPriceInfo;
    }, [prepareRoundingPayload])

    const handleRounding = useCallback(async (roundingBehviour: string, roundingPrecision: string) => {
        if (roundingBehviour === NO_ROUNDING)
            return;

        const gridRefs = [...Object.values(mainProductRef?.current || {}), ...Object.values(optionProductRef.current || {})].map((obj) => obj);

        const payload: RoundingPayloadRequest[] = [];
        // This data structure will contain productId as key and product object as value if it is a product line item or else if its a kit then kit id will be key and value will be array of components with item at zeroth index being kit object.
        const productKitsAndComponentsMap = new Map();

        gridRefs.forEach((gridRef) => {
            gridRef?.api?.forEachNodeAfterFilterAndSort(function (rowNode: RowNode) {
                if (rowNode?.data) {
                    const data = { ...rowNode.data };
                    if (data?.is_kit && !productKitsAndComponentsMap.has(data?.id)) {
                        productKitsAndComponentsMap.set(data?.id, [data]);
                    } else if (data?.kit && productKitsAndComponentsMap.has(data.kit)) {
                        const components = productKitsAndComponentsMap.get(data.kit);
                        if (Array.isArray(components)) {
                            components.push(data)
                        }
                    } else {
                        productKitsAndComponentsMap.set(data?.id, data)
                    }
                }
            });
        });

        if (productKitsAndComponentsMap.size) {
            for (let item of productKitsAndComponentsMap.values()) {
                // If item is components array belonging to a KIT
                if (Array.isArray(item)) {
                    const [kit] = item.slice(0, 1);
                    const components = item.slice(1);
                    for (let i = 0; i < components.length; i++) {
                        // processing component line item for rounding payload
                        components[i] = processDataForRoundngPayload(payload, components[i], roundingBehviour, roundingPrecision)
                    }
                    // updating kit parent line item sell price
                    kit.sellPrice = components.reduce((acc: number, curr: any) => {
                        acc += ((curr.sellPrice || 0) * (curr.qty || 0))
                        return acc;
                    }, 0);
                    // processing kit parent line item for rounding payload
                    processDataForRoundngPayload(payload, kit, roundingBehviour, roundingPrecision)
                } else {
                    // processing product line item for rounding payload
                    processDataForRoundngPayload(payload, item, roundingBehviour, roundingPrecision)
                }
            }
        }

        const lotsPayload = lotApiData?.data.map((lotPriceRow: LotsResponse) => {
            const isSellPrice = roundingBehviour === ROUNDING_SELL_PRICE_KEY;
            const roundedSellPrice = isSellPrice && lotPriceRow?.sell_price_cents ? Number(applyRounding(convertToDollar(lotPriceRow?.sell_price_cents), roundingPrecision as RoundingBehaviour)) : lotPriceRow.sell_price_cents;
            return {
                id: lotPriceRow.id,
                name: lotPriceRow.name || '',
                cost_cents: lotPriceRow.cost_cents,
                discount_percentage: lotPriceRow.discount_percentage,
                discounted_cost_cents: lotPriceRow.discounted_cost_cents,
                lead_time_unit: lotPriceRow.lead_time_unit,
                lead_time_min: lotPriceRow.lead_time_min,
                lead_time_max: lotPriceRow.lead_time_max,
                source_quote_number: lotPriceRow.source_quote_number,
                margin_percentage: lotPriceRow.discounted_cost_cents && roundedSellPrice ? Number(((1 - lotPriceRow.discounted_cost_cents / convertToCents(roundedSellPrice)) * 100).toFixed(2)) : null,
                sell_price_cents: lotPriceRow.sell_price_cents && roundedSellPrice ? convertToCents(roundedSellPrice) : lotPriceRow.sell_price_cents,
            };

        });
        const request: any = {
            bomId: bomId!,
            payload: {
                add: [],
                edit: lotsPayload,
                delete: []
            }
        };

        await saveLotDetails(request);
        if (payload.length > 0) {
            await patchBulkEdit({ bomId: Number(bomId), payload });
        }
    }, [bomId, lotApiData?.data, patchBulkEdit, processDataForRoundngPayload, saveLotDetails])

    const isBomLinked = projectBomsMappedData.get(Number(bomId));

    const onColumnSelection = async (rowData: string[]) => {
        try {
            await saveColumnOrderEvents("bom_pricing_tab", rowData)
        } catch (e) {
            console.error(e);
        } finally {
            setOpenManageColumnsPopup(() => false);
        }
    };

    const allColumnOptions = useMemo(() => {
        const fieldsToExcludeIfNoQuotingAccess = ['quoteDueDate', 'quoteOwner', 'product_to_replace'];
        if (!enable_quoting) {
            const res = COLUMN_OPTIONS_PL_TAB.data.filter((item) => !fieldsToExcludeIfNoQuotingAccess.includes(item.field));
            return res;
        }

        return COLUMN_OPTIONS_PL_TAB.data;
    }, [enable_quoting]);

    const onManageColumnsClick = () => {
        setOpenManageColumnsPopup(true);
    };

    const notificationCount = useMemo(() => {

        if (isColumnDataLoading) return 0

        const selectedColumnsData = columnOrderDetails && columnOrderDetails?.order;
        const hiddenColumns = allColumnOptions.filter((item: {
            label: string;
            field: ColumnKeys;
        }) => !(selectedColumnsData || []).includes(item?.field));
        const defaultKeys = Object.keys(DEFAULT_VALUES_WARNING);

        const filteredHiddenColumns = hiddenColumns.filter(column =>
            defaultKeys.includes(column.field)
        );

        const fieldsToCheck = filteredHiddenColumns.map(column => column.field);
        const nonZeroCount = checkDefaultValuesWarningCount(unProcessedTableData || [], fieldsToCheck);

        return nonZeroCount;

    }, [isColumnDataLoading, columnOrderDetails, allColumnOptions, unProcessedTableData])

    const getSectionBodItems = useCallback((sectionId: number, isOptionTable?: boolean) => {
        const data = isOptionTable ? option : section;
        const key = isOptionTable ? 'substitute_section' : "section";
        const sectionItems = (data || []).filter((item: ProcessedItem) => item[key as keyof typeof item] === sectionId);
        return sectionItems || []
    }, [option, section])

    const onChangeTableView = useCallback(
        (value: ViewOptionValues, isOptionsTable: boolean) => {
            const tableKey: keyof TablesView = isOptionsTable ? "optionsTable" : "productTable";
            setTablesView(prev => ({
                ...prev,
                [tableKey]: value,
            }));
        },
        []
    );

    const accOptions = useMemo(() => {
        const dynamicTableMaxHeight = tableContainerDimensions
            ? Math.round(tableContainerDimensions?.height - 102)
            : 300;

        return [
            {
                labelId: 'Products',
                summary:
                    <Box pl={2} position="sticky" top="0" zIndex="3" >
                        <H5>Products</H5>
                    </Box>,
                details:
                    <Box mt={2}>
                        {tablesView.productTable === VIEW_OPTIONS.SECTION ? (
                            <MainProductTable
                                optionSection={optionsApiData?.data || []}
                                tableData={section as unknown as MainProductDataType[]}
                                isLoading={isDataLoading}
                                onPricingInfoEdit={handlePricingInfoEdit}
                                onBomInfoEdit={handleBomInfoEdit}
                                onNewLot={handleNewLot}
                                onLotUpdate={handleLotUpdate}
                                tableMaxHeight={dynamicTableMaxHeight}
                                mainProductOptionsInfo={mainProductOptionsInfo}
                                handleTablesReordering={handleTablesReordering}
                                handleTablesWidthChanged={handleTablesWidthChanged}
                                selectedRowsRef={selectedRowsRef}
                                getSectionBodItems={getSectionBodItems}
                                currentGroupWhilePastingRef={currentGroupWhilePastingRef}
                                ref={mainProductRef}
                                currentTabValue={currentTabValue}
                                onChangeTableView={(value) => onChangeTableView(value, false)}
                            />
                        ) : (
                            <ManufacturerViewTable
                                bods={unProcessedTableData}
                                sectionKey='section'
                                lotData={lotApiData?.data}
                                isTableLoading={isDataLoading}
                                onChangeTableView={(value) => onChangeTableView(value, false)}
                                tableContainerDimensions={tableContainerDimensions}
                                sections={sectionData?.data}
                            />
                        )}
                    </Box>,
                rightSummary:
                    <Box width="100%" display="flex" flexDirection="row-reverse" gap={2}>
                        <ImportProducts color="secondary" variant='outlined' sx={{ bgcolor: "white" }} />
                        {tablesView.productTable === VIEW_OPTIONS.SECTION ? (
                            <KebabNestedMenu
                                onClickManageColumn={onManageColumnsClick}
                                notificationCount={notificationCount}
                                onRoundingSubmit={handleRounding}
                                isLoading={isRoundingLoading || isSaveLoading}
                                hasOption={option?.length > 0}
                            />
                        ) : null}
                    </Box >
            },
            // Hide the Options section, when manufacturer view is opened in the product's table
            ...(tablesView.productTable === VIEW_OPTIONS.MANUFACTURER ? [] : [{
                labelId: 'Options',
                summary: <Box pl={2} position="sticky" top="0" zIndex="3" >
                    <H5>Options</H5>
                </Box>,
                details:
                    <Box mt={2}>
                        <OptionTable
                            tableData={(option || []) as unknown as OptionDataType[]}
                            isLoading={isDataLoading}
                            tableMaxHeight={dynamicTableMaxHeight}
                            onPricingInfoEdit={handlePricingInfoEdit}
                            onBomInfoEdit={handleBomInfoEdit}
                            handleTablesReordering={handleTablesReordering}
                            handleTablesWidthChanged={handleTablesWidthChanged}
                            pageName={'bom_pricing_tab'}
                            selectedRowsRef={selectedRowsRef}
                            currentGroupWhilePastingRef={currentGroupWhilePastingRef}
                            ref={optionProductRef}
                            getSectionBodItems={getSectionBodItems}
                            currentTabValue={currentTabValue}
                        />
                    </Box>
            }])

        ]
    }, [tableContainerDimensions, tablesView.productTable, optionsApiData?.data, section, isDataLoading, handlePricingInfoEdit, handleBomInfoEdit, handleNewLot, handleLotUpdate, mainProductOptionsInfo, handleTablesReordering, handleTablesWidthChanged, getSectionBodItems, currentTabValue, unProcessedTableData, sectionData?.data, lotApiData?.data, notificationCount, handleRounding, isRoundingLoading, isSaveLoading, option, onChangeTableView])

    return (
        <>
            {isBomLinked ? (
                <Box flex={1} mx={4} display="flex" flexDirection="column" minHeight={0} border='1px solid' borderColor='neutral.main' borderRadius={2} sx={{ borderTopLeftRadius: 0 }} bgcolor='white'>
                    <LinkedBomsPopup primaryBomId={primaryBomId || 0} isbomLoading={isLoading} />
                </Box>
            ) : (
                <>
                    <Box flex={1} mx={4} display="flex" flexDirection="column" minHeight={0} border='1px solid' borderColor='neutral.main' sx={{ borderTopLeftRadius: 0 }} borderRadius={2}>
                        <Snackbar open={!!snackbarMessage} onClose={() => setSnackbarMessage('')} message={snackbarMessage}></Snackbar>
                        {isBomLinked && <LinkedBomsPopup primaryBomId={primaryBomId || 0} isbomLoading={isLoading} />}
                        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                            <Box flex={1} display="flex" minHeight={0} ref={resizeNodeSetter} flexDirection="column" overflow='auto'>
                                <ProductOrOptionTableContent bomId={bomId} options={accOptions} />
                            </Box>
                        </Box>
                    </Box >
                    <Box width="100%" display="flex" gap={4} pt={2.5} pl={6} pr={18} pb={0} bgcolor="tertiary.contrastText">
                        {cardDataArr.map(({ title, amountForUi, helperText, editIconCallBack, loading }) =>
                            loading ? (
                                <Skeleton variant="rectangular" width="25%" height="100%" />
                            ) : (
                                <ModalCard title={title} amount={amountForUi} helpertext={helperText} editIconCallback={editIconCallBack} />
                            )
                        )}
                    </Box>
                    <Footer statsData={statsData} />
                    {
                        openManageColumnsPopup && (
                            <ManageColumnsPopup
                                isOpen={openManageColumnsPopup}
                                setIsOpen={setOpenManageColumnsPopup}
                                allColumnOptions={[...allColumnOptions]}
                                setSnackbarData={setSnackbarMessage}
                                onColumnSelection={onColumnSelection}
                                isSelectedColumnsMutationLoading={isColumnDataLoading}
                                selectedColumnsData={columnOrderDetails && columnOrderDetails?.order}
                                isSelectedColumnsQueryDataFetching={isSelectedColumnsAndFiltersQueryDataFetching}
                                tab={true}
                                tableData={unProcessedTableData}
                            />
                        )
                    }
                    {openLotModal && <LotPricingModal open={openLotModal} onClose={() => setOpenLotModal(false)} />}
                    {openServicesModal && <ServiceModalWrapper totalExtendedPrice={Number(totalExtendedPrice.toFixed(2))} open={openServicesModal} onClose={() => setOpenServicesModal(false)} />}
                    {
                        openTaxesModal && (
                            <TaxesModal
                                open={openTaxesModal}
                                onClose={() => setOpenTaxesModal(false)}
                                totalExtendedPrice={Number(totalExtendedPrice.toFixed(2))}
                                servicesTotal={Number(serviceCardData.amount || 0)}
                                freightTotal={Number(freightCardData.amount || 0)}
                            />
                        )
                    }
                    {openFreightModal && <FreightModal totalExtendedPrice={Number(totalExtendedPrice.toFixed(2))} open={openFreightModal} onClose={() => setOpenFreightModal(false)} />}
                </>

            )
            }
        </>
    );
}

